export type AlarmTone = 'hospital_chirp' | 'piercing_siren' | 'urgent_pulse' | 'fire_engine_siren';

export function toneForCode(name: string, fallback: AlarmTone = 'hospital_chirp'): AlarmTone {
  return /\b(code\s+red|fire|smoke)\b/i.test(name) ? 'fire_engine_siren' : fallback;
}

export class BrowserAlarm {
  private context: AudioContext | null = null;
  private nodes = new Set<OscillatorNode>();
  private timer: ReturnType<typeof setInterval> | null = null;
  private generation = 0;
  private tone: AlarmTone | null = null;
  private settings = { soundEnabled: true, vibrationEnabled: true, volume: 1 };
  configure(settings: typeof this.settings) {
    if (settings.volume !== this.settings.volume || settings.soundEnabled !== this.settings.soundEnabled) this.stopAlarm();
    this.settings = settings;
  }
  async enable() {
    const Ctx = globalThis.AudioContext || (globalThis as any).webkitAudioContext;
    if (!Ctx) throw new Error('Audio is not supported on this device');
    if (!this.context || this.context.state === 'closed') this.context = new Ctx();
    await this.context!.resume();
    if (this.context!.state !== 'running') throw new Error('Tap Enable sound to allow alerts');
  }
  private async cycle(tone: AlarmTone, generation: number) {
    try {
      if (!this.settings.soundEnabled) return;
      await this.enable();
      if (generation !== this.generation) return;
      const ctx = this.context!;
      const now = ctx.currentTime;
      const segments = tone === 'urgent_pulse' ? [0, .25, .5] : [0];
      for (const offset of segments) {
        const osc = ctx.createOscillator(), gain = ctx.createGain();
        const start = now + offset;
        const duration = tone === 'fire_engine_siren' ? 2.2 : tone === 'urgent_pulse' ? .2 : .7;
        osc.type = 'sine';
        if (tone === 'fire_engine_siren') {
          osc.frequency.setValueAtTime(620, start);
          osc.frequency.linearRampToValueAtTime(1050, start + 1.1);
          osc.frequency.linearRampToValueAtTime(620, start + 2.2);
        } else if (tone === 'piercing_siren') {
          osc.frequency.setValueAtTime(950, start);
          osc.frequency.linearRampToValueAtTime(1800, start + duration);
        } else {
          osc.frequency.setValueAtTime(tone === 'urgent_pulse' ? 1600 : 920, start);
          if (tone === 'hospital_chirp') osc.frequency.setValueAtTime(1480, start + .25);
        }
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(.25 * Math.max(0, Math.min(1, this.settings.volume)), start + .01);
        gain.gain.setValueAtTime(.25 * Math.max(0, Math.min(1, this.settings.volume)), start + duration - .01);
        gain.gain.linearRampToValueAtTime(0, start + duration);
        osc.connect(gain); gain.connect(ctx.destination);
        this.nodes.add(osc);
        osc.onended = () => { this.nodes.delete(osc); osc.disconnect(); gain.disconnect(); };
        osc.start(start); osc.stop(start + duration);
      }
    } catch (error) { console.warn('Alarm audio unavailable; visual alert remains active', error); }
  }
  startAlarm(tone: AlarmTone = 'hospital_chirp') {
    if (this.tone === tone) return;
    this.stopAlarm(); this.tone = tone;
    const generation = this.generation;
    void this.cycle(tone, generation);
    this.timer = setInterval(() => void this.cycle(tone, generation), 2200);
  }
  playAlertBeep(tone: AlarmTone = 'hospital_chirp') {
    if (!this.tone) return this.cycle(tone, this.generation);
  }
  playAckChirp() {}
  stopAlarm() {
    this.generation++; this.tone = null;
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    for (const node of this.nodes) { try { node.stop(); } catch {} node.disconnect(); }
    this.nodes.clear();
  }
  get isAlarming() { return this.tone !== null; }
}
export default new BrowserAlarm();
